import { cmd, http } from "elm-ts"
import * as nav from "elm-ts/lib/Navigation"
import { Model, Msg, Page } from "./Model"
import * as M from "./Model"
import * as TE from "fp-ts/TaskEither"
import * as ET from "elm-ts/lib/Task"
import * as O from "fp-ts/Option"
import { tuple } from "fp-ts"
import { flow, pipe } from "fp-ts/function"
import { Variant, IO } from "@flypengine/myFlyp-lib"
import * as Dashboard from "./Dashboard"
import * as Login from "./Login"
import * as Router from "./routes"
import * as E from "fp-ts/Either"
import * as FirebaseAuth from "firebase/auth"
import { AuthStatus, logoutUser } from "./Authorization"
import * as Http from "./Http"
import { API_BASE_URL } from "./runtimeVariables"
import * as t from "io-ts"

const initModel: (location: nav.Location) => Model = location =>
  M.Model.Auth({
    nextLocation: location,
    login: Login.initModel(),
  })

export const init: (loc: nav.Location) => [Model, cmd.Cmd<Msg>] = location => [
  initModel(location),
  cmd.none,
]

export const update = (msg: Msg, model: Model): [Model, cmd.Cmd<Msg>] => {
  return pipe(
    msg,
    Variant.caseOf({
      [M._resetNavigationMsg]: () => {
        location.hash = ""
        return [model, cmd.none]
      },
      [M._navigationMsg]: nextLocation =>
        pipe(
          model,
          Variant.caseOfWithDefault<[Model, cmd.Cmd<Msg>]>([model, cmd.none])({
            [M._App]: app =>
              pipe(
                initApp(app.user)(nextLocation)(O.some(app)),
                tuple.bimap(newCmd => newCmd, M.Model.App)
              ),
          })
        ),
      [M._authStateChangedMsg]: payload =>
        pipe(
          model,
          Variant.caseOfWithDefault<[M.Model, cmd.Cmd<M.Msg>]>([
            model,
            cmd.none,
          ])({
            [M._Auth]: auth =>
              pipe(
                payload,
                O.match(
                  () =>
                    pipe(
                      Login.init(M.configLogin),
                      tuple.bimap(cmd.map(M.Msg.loginMsg), login =>
                        M.Model.Auth({ ...auth, login })
                      )
                    ),
                  user =>
                    pipe(
                      initApp(user)(auth.nextLocation)(O.none),
                      tuple.bimap(
                        newCmd =>
                          cmd.batch([
                            newCmd,
                            pipe(
                              user.metadata.lastSignInTime,
                              O.fromNullable,
                              O.match(
                                () => cmd.none,
                                lastSignInTime =>
                                  pipe(
                                    http.post(
                                      `${API_BASE_URL}/myFlyp/login`,
                                      { lastSignInTime },
                                      flow(
                                        t.string.decode,
                                        E.mapLeft(IO.validationErrorsToString)
                                      )
                                    ),
                                    Http.sendRequest(user)(_ => M.Msg.noOp())
                                  )
                              )
                            ),
                            nav.push(auth.nextLocation.pathname),
                          ]),
                        M.Model.App
                      )
                    )
                )
              ),
          })
        ),

      [M._logout]: () =>
        pipe(
          model,
          Variant.caseOfWithDefault<[M.Model, cmd.Cmd<M.Msg>]>([
            model,
            cmd.none,
          ])({
            [M._App]: app => [
              model,
              cmd.batch([
                pipe(
                  app.user.metadata.lastSignInTime,
                  O.fromNullable,
                  O.match(
                    () => cmd.none,
                    lastSignInTime =>
                      pipe(
                        {
                          method: "PUT",
                          url: `${API_BASE_URL}/myFlyp/login`,
                          body: { lastSignInTime },
                          timeout: O.none,
                          headers: {},
                          withCredentials: false,
                          expect: flow(
                            t.string.decode,
                            E.mapLeft(IO.validationErrorsToString)
                          ),
                        },
                        Http.sendRequest(app.user)(_ => M.Msg.noOp())
                      )
                  )
                ),
                pipe(
                  TE.tryCatch(
                    () => logoutUser(),
                    error => error!["message"] as AuthStatus
                  ),
                  ET.attempt(result => M.Msg.logoutRes(result))
                ),
              ]),
            ],
          })
        ),
      [M._logoutRes]: res => {
        console.log(res)
        return pipe(
          res,
          E.match(
            () => [model, cmd.none],
            () => [
              M.Model.Auth({
                nextLocation: new URL("/", location.origin),
                login: Login.initModel("Login"),
              }),
              cmd.none,
            ]
          )
        )
      },
      [M._loginMsg]: loginMsg =>
        pipe(
          model,
          Variant.caseOfWithDefault<[M.Model, cmd.Cmd<M.Msg>]>([
            model,
            cmd.none,
          ])({
            [M._Auth]: auth =>
              pipe(
                Login.update(M.configLogin)(loginMsg)(auth.login),
                tuple.bimap(
                  newCmd => newCmd,
                  loginModel => M.Model.Auth({ ...auth, login: loginModel })
                )
              ),
          })
        ),
      [M._dashboardMsg]: dashboardMsg =>
        pipe(
          model,
          Variant.caseOfWithDefault<[M.Model, cmd.Cmd<M.Msg>]>([
            model,
            cmd.none,
          ])({
            [M._App]: app =>
              pipe(
                app.contents,
                Variant.caseOfWithDefault<[M.Model, cmd.Cmd<M.Msg>]>([
                  model,
                  cmd.none,
                ])({
                  [M.Page.Dashboard.name]: (dashboardModel: Dashboard.Model) =>
                    pipe(
                      Dashboard.update(
                        M.configDashboard({
                          user: app.user,
                          nextLocation: app.location,
                        })
                      )(dashboardMsg)(dashboardModel),
                      tuple.bimap(
                        dashboardCmd => dashboardCmd,
                        dashboardNewModel =>
                          M.Model.App({
                            ...app,
                            contents: Page.Dashboard(dashboardNewModel),
                          })
                      )
                    ),
                })
              ),
          })
        ),
      [M._noOp]: () => [model, cmd.none],
    })
  )
}

const initApp =
  (user: FirebaseAuth.User) =>
  (nextLocation: nav.Location) =>
  (app: O.Option<M.App>): [M.App, cmd.Cmd<M.Msg>] => {
    switch (Router.parseLocation(nextLocation.pathname)._tag) {
      case "Home":
      case "Dashboard": {
        return Router.isAllowedRoute(nextLocation.pathname)
          ? pipe(
              app,
              O.chain(({ contents }) =>
                pipe(
                  contents,
                  Variant.caseOfWithDefault(O.none)({
                    [M.Page.Dashboard.name]: flow(O.of),
                  })
                )
              ),
              prevContents =>
                pipe(
                  Dashboard.init(M.configDashboard({ nextLocation, user }))(
                    prevContents
                  ),
                  tuple.bimap(cmd.map(M.Msg.dashboardMsg), dashboardModel => ({
                    user,
                    location: nextLocation,
                    contents: Page.Dashboard(dashboardModel),
                  }))
                )
            )
          : [
              {
                user,
                location: nextLocation,
                contents: Page.NotFound(),
              },
              cmd.none,
            ]
      }
      case "NotFound": {
        return [
          {
            user,
            location: nextLocation,
            contents: Page.NotFound(),
          },
          cmd.none,
        ]
      }
    }
  }
